import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { Delete } from '@factorialco/factorial-one-react/icons/app'
import {
  AtsDeleteMessageTemplateDocument,
  AtsMessageTemplatesListDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { messageTemplateVisibilityLabel } from 'modules/ats/lib/utils/messageTemplates'

interface MessageTemplatesDataTableProps {
  canManageCompanyMessageTemplates: boolean
}

export const MessageTemplatesDataTable = ({
  canManageCompanyMessageTemplates,
}: MessageTemplatesDataTableProps) => {
  const { navigation } = useNavigation()
  const { openDialog } = useConfirmationDialog()

  const [deleteMessageTemplate] = useMutation(AtsDeleteMessageTemplateDocument, {
    extractorFn: (data) => data.atsMutations.deleteAtsMessageTemplate,
  })

  const removeMessageTemplate = async (id: number) => {
    const result = await deleteMessageTemplate({
      variables: { id },
      update: (cache, { data }) => {
        if (!data?.atsMutations.deleteAtsMessageTemplate.messageTemplate?.id) return
        cache.evict({
          id: cache.identify(data.atsMutations.deleteAtsMessageTemplate.messageTemplate),
        })
      },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handled
  }

  const source = useGraphqlDataSource({
    document: AtsMessageTemplatesListDocument,
    mapData: (data) => data.ats.messageTemplatesConnection.nodes,
    search: {
      enabled: true,
    },
    mapVariables: ({ sortings, search, filters }) => {
      const visibility = filters.visibility
      const visibilityFilter = visibility ? { in: visibility } : undefined

      return {
        search: search,
        sort: sortings?.[0],
        visibility: visibilityFilter,
      }
    },
    presets: [
      {
        label: i18n.t('ats.manage_message_templates.visibility.company'),
        filter: {
          visibility: ['company'],
        },
      },
      {
        label: i18n.t('ats.manage_message_templates.visibility.personal'),
        filter: {
          visibility: ['personal'],
        },
      },
    ],
    mapPagination: (data) => ({
      total: data.ats.messageTemplatesConnection.totalCount ?? 0,
    }),

    filters: {
      visibility: {
        type: 'in',
        label: i18n.t('ats.manage_message_templates.table_headers.type'),
        options: {
          options: [
            {
              label: i18n.t('ats.manage_message_templates.visibility.company'),
              value: 'company',
            },
            {
              label: i18n.t('ats.manage_message_templates.visibility.personal'),
              value: 'personal',
            },
          ],
        },
      },
    },
    sortings: {
      name: {
        label: i18n.t('ats.manage_message_templates.table_headers.name'),
      },
    },
    itemUrl: (messageTemplate) =>
      navigation.settings.ats.templates.messageTemplatesId.fullPath({
        messageTemplateId: messageTemplate.id,
      }),
    itemActions: (messageTemplate) => {
      const canDeleteMessage =
        messageTemplate.visibility === 'personal' || canManageCompanyMessageTemplates

      return canDeleteMessage
        ? [
            {
              label: i18n.t('common.delete'),
              icon: Delete,
              critical: true,
              onClick: () => {
                openDialog({
                  illustration: 'delete',
                  title: i18n.t('ats.manage_message_templates.delete_modal.delete_title', {
                    name: messageTemplate.name,
                  }),
                  content: i18n.t('ats.manage_message_templates.delete_modal.description'),
                  confirmLabel: i18n.t('ats.manage_message_templates.delete_modal.delete_button'),
                  cancelLabel: i18n.t('common.cancel'),
                  onConfirm: () => removeMessageTemplate(messageTemplate.id),
                })
              },
            },
          ]
        : undefined
    },
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('ats.manage_message_templates.table_headers.name'),
                render: ({ name }) => name,
                sorting: 'name',
              },
              {
                label: i18n.t('ats.manage_message_templates.table_headers.author'),
                render: ({ authorFullName, authorAvatar }) => ({
                  type: 'person',
                  value: {
                    firstName: authorFullName ?? '',
                    lastName: '',
                    src: buildUrl(authorAvatar),
                  },
                }),
              },
              {
                label: i18n.t('ats.manage_message_templates.table_headers.type'),
                render: ({ visibility }) => messageTemplateVisibilityLabel(visibility),
              },
            ],
          },
        },
      ]}
    />
  )
}
